import { useEffect, useState } from 'react'
import { Alert } from 'reactstrap'
import {
  revokeVehicleCert,
  VehicleCert,
  VehicleCertQuery
} from '../../services/endpoints/service29'
import useCertList from './useCertList'
import DataGrid, {
  Column,
  LoadPanel,
  Sorting
} from 'devextreme-react/data-grid'
import { makeStatusRenderer, renderDate, renderUser } from './Service29Utils'
import CertPager from './CertPager'
import PageSizeSelect from './PageSizeSelect'
import CertSelectAll from './CertSelectAll'

const BASE_QUERY: VehicleCertQuery = {
  status: ['issued', 'installed', 'pending_revocation'],
  sort: 'issuedAt',
  order: 'desc',
  max: 50,
  offset: 0
}

const renderStatus = makeStatusRenderer({
  issued: { label: 'Issued', color: 'success' },
  installed: { label: 'Installed', color: 'primary' },
  pending_revocation: { label: 'Pending Revocation', color: 'warning' }
})

const Service29ActiveTab = () => {
  const [query, setQuery] = useState<VehicleCertQuery>(BASE_QUERY)
  const { certs, setCerts, total, error } = useCertList(true, query)
  const [alert, setAlert] = useState<{ message: string; color: string } | null>(
    null
  )
  const [selectedCerts, setSelectedCerts] = useState<VehicleCert[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)

  useEffect(() => {
    setSelectedCerts([])
  }, [query])

  const toggleCert = (cert: VehicleCert) => {
    setSelectedCerts((prev) =>
      prev.some((c) => c.id === cert.id)
        ? prev.filter((c) => c.id !== cert.id)
        : [...prev, cert]
    )
  }

  const handleBulkRevoke = async () => {
    if (!window.confirm(`Revoke ${selectedCerts.length} cert(s)?`)) return
    setBulkLoading(true)
    const results = await Promise.allSettled(
      selectedCerts.map((c) => revokeVehicleCert(c.id))
    )
    const succeededIds = selectedCerts
      .filter((_, i) => results[i].status === 'fulfilled')
      .map((c) => c.id)
    const failedCount = results.filter((r) => r.status === 'rejected').length

    if (succeededIds.length > 0) {
      setCerts((prev) =>
        prev.map((c) =>
          succeededIds.includes(c.id)
            ? { ...c, status: 'pending_revocation' }
            : c
        )
      )
    }
    setSelectedCerts([])
    setBulkLoading(false)

    if (failedCount > 0) {
      setAlert({
        message: `${succeededIds.length} revoked, ${failedCount} failed`,
        color: 'warning'
      })
    } else {
      setAlert({
        message: `${succeededIds.length} cert(s) revocation initiated`,
        color: 'success'
      })
    }
  }

  return (
    <section className="cert-tab-section">
      {(alert || error) && (
        <Alert color={alert?.color ?? 'danger'} toggle={() => setAlert(null)}>
          {alert?.message ?? error}
        </Alert>
      )}
      <menu className="cert-tab-toolbar">
        <li>
          {selectedCerts.length > 0 && (
            <button
              className="cert-bulk-action revoke"
              disabled={bulkLoading}
              onClick={handleBulkRevoke}
            >
              {bulkLoading
                ? 'Revoking…'
                : `Revoke Selected (${selectedCerts.length})`}
            </button>
          )}
        </li>
        <li>
          <PageSizeSelect
            value={query.max ?? 50}
            onChange={(max) =>
              setQuery((prev) => ({ ...prev, max, offset: 0 }))
            }
          />
        </li>
      </menu>
      <DataGrid
        dataSource={certs}
        keyExpr="id"
        showBorders={true}
        rowAlternationEnabled={true}
        columnAutoWidth={true}
        allowColumnResizing={true}
      >
        <LoadPanel enabled />
        <Sorting mode="none" />
        <Column dataField="vehicle.displayName" caption="Vehicle" />
        <Column dataField="vehicle.vin" caption="VIN" />
        <Column dataField="device.serial" caption="Device" />
        <Column dataField="status" caption="Status" cellRender={renderStatus} />
        <Column
          dataField="issuedBy"
          caption="Issued By"
          cellRender={renderUser}
        />
        <Column
          dataField="issuedAt"
          caption="Issued At"
          cellRender={renderDate}
        />
        <Column
          dataField="certExpiresAt"
          caption="Expires At"
          cellRender={renderDate}
        />
        <Column
          width={100}
          allowSorting={false}
          headerCellRender={() => (
            <CertSelectAll
              certs={certs}
              eligibleStatus="installed"
              variant="revoke"
              label="Select All"
              selectedCerts={selectedCerts}
              onChange={setSelectedCerts}
            />
          )}
          cellRender={({ data }: { data: VehicleCert }) => {
            if (data.status !== 'installed') return null
            const selected = selectedCerts.some((c) => c.id === data.id)
            return (
              <>
                <input
                  type="checkbox"
                  id={`cert-select-${data.id}`}
                  className="cert-select-check revoke"
                  checked={selected}
                  onChange={() => toggleCert(data)}
                />
                <label htmlFor={`cert-select-${data.id}`}>Revoke</label>
              </>
            )
          }}
        />
      </DataGrid>
      <CertPager
        total={total}
        offset={query.offset ?? 0}
        max={query.max ?? 50}
        onPageChange={(offset) => setQuery((prev) => ({ ...prev, offset }))}
      />
    </section>
  )
}

export default Service29ActiveTab
